import { create } from 'zustand';
import type { Project, ProjectFile, ProjectMeta, ProjectSource, ProjectPrefabConfig, ProjectRegistrationStatus, RegisteredModule } from '../types/project';
import { useAuthStore } from './useAuthStore';

const DB_NAME = 'rivellum-console-projects';
const DB_VERSION = 1;
const STORE_NAME = 'projects';

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function persistProject(project: Project): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(project);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function loadAllProjects(): Promise<Project[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function deleteProjectFromDB(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function generateId(): string {
  return crypto.randomUUID();
}

// ─── Workspace state persistence ────────────────────────────────
const WS_KEY = 'rivellum-console-workspace';

interface WorkspaceState {
  activeProjectId: string | null;
  openFileIds: string[];
  activeFileId: string | null;
}

function saveWorkspaceState(state: Partial<WorkspaceState>): void {
  try {
    const raw = localStorage.getItem(WS_KEY);
    const current: WorkspaceState = raw ? JSON.parse(raw) : { activeProjectId: null, openFileIds: [], activeFileId: null };
    localStorage.setItem(WS_KEY, JSON.stringify({ ...current, ...state }));
  } catch { /* ignore */ }
}

function loadWorkspaceState(): WorkspaceState {
  try {
    const raw = localStorage.getItem(WS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { activeProjectId: null, openFileIds: [], activeFileId: null };
}

function clearWorkspaceState(): void {
  try { localStorage.removeItem(WS_KEY); } catch { /* ignore */ }
}

function detectLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    ts: 'typescript', tsx: 'typescriptreact', js: 'javascript', jsx: 'javascriptreact',
    rs: 'rust', toml: 'toml', json: 'json', md: 'markdown', css: 'css',
    html: 'html', yaml: 'yaml', yml: 'yaml', sh: 'shell', mist: 'mist',
    sol: 'solidity', txt: 'plaintext',
  };
  return map[ext] ?? 'plaintext';
}

interface ProjectState {
  projects: ProjectMeta[];
  activeProjectId: string | null;
  activeProject: Project | null;
  openFileIds: string[];
  activeFileId: string | null;
  isLoading: boolean;
  error: string | null;

  loadProjects: () => Promise<void>;
  createProject: (name: string, source: ProjectSource, files?: ProjectFile[]) => Promise<string>;
  openProject: (id: string) => Promise<void>;
  closeProject: () => void;
  deleteProject: (id: string) => Promise<void>;
  renameProject: (id: string, name: string) => Promise<void>;

  addFile: (name: string, path: string, content?: string) => Promise<void>;
  updateFileContent: (fileId: string, content: string) => void;
  deleteFile: (fileId: string) => Promise<void>;
  openFile: (fileId: string) => void;
  closeFile: (fileId: string) => void;
  setActiveFile: (fileId: string) => void;
  saveProject: () => Promise<void>;

  addPrefabToProject: (config: ProjectPrefabConfig) => Promise<void>;
  removePrefabFromProject: (prefabId: string) => Promise<void>;
  setRegistrationStatus: (status: ProjectRegistrationStatus, projectId?: string, modules?: RegisteredModule[]) => Promise<void>;
  registerModule: (prefabId: string, module: RegisteredModule) => Promise<void>;
  hasRegisteredModules: () => boolean;
}

export const useProjectStore = create<ProjectState>()((set, get) => ({
  projects: [],
  activeProjectId: null,
  activeProject: null,
  openFileIds: [],
  activeFileId: null,
  isLoading: false,
  error: null,

  loadProjects: async () => {
    set({ isLoading: true, error: null });
    try {
      const all = await loadAllProjects();
      const currentAddress = useAuthStore.getState().address;
      // Filter to projects owned by current wallet (or legacy unowned projects)
      const owned = currentAddress
        ? all.filter(p => !p.owner || p.owner === currentAddress)
        : all;
      const metas: ProjectMeta[] = owned.map(p => ({
        id: p.id,
        name: p.name,
        source: p.source,
        createdAt: p.createdAt,
        updatedAt: p.updatedAt,
        owner: p.owner,
        fileCount: p.files.length,
        prefabCount: (p.addedPrefabs ?? []).length,
        registrationStatus: p.registrationStatus ?? 'draft',
      }));
      set({ projects: metas, isLoading: false });

      // Restore workspace state (re-open last project + files)
      const ws = loadWorkspaceState();
      if (ws.activeProjectId && !get().activeProject) {
        const project = owned.find(p => p.id === ws.activeProjectId);
        if (project) {
          // Validate that saved file IDs still exist in the project
          const validFileIds = new Set(project.files.map(f => f.id));
          const openFileIds = ws.openFileIds.filter(id => validFileIds.has(id));
          const activeFileId = ws.activeFileId && validFileIds.has(ws.activeFileId) ? ws.activeFileId : (openFileIds[0] ?? null);
          set({
            activeProjectId: ws.activeProjectId,
            activeProject: project,
            openFileIds,
            activeFileId,
          });
        }
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load projects', isLoading: false });
    }
  },

  createProject: async (name, source, files) => {
    const id = generateId();
    const now = Date.now();
    const owner = useAuthStore.getState().address ?? undefined;
    const project: Project = {
      id,
      name,
      source,
      createdAt: now,
      updatedAt: now,
      owner,
      files: files ?? [],
      rootFolders: [],
      addedPrefabs: [],
      registrationStatus: 'draft',
    };
    await persistProject(project);
    const meta: ProjectMeta = { id, name, source, createdAt: now, updatedAt: now, owner, fileCount: project.files.length, prefabCount: 0, registrationStatus: 'draft' };
    set(s => ({ projects: [...s.projects, meta] }));
    return id;
  },

  openProject: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const all = await loadAllProjects();
      const project = all.find(p => p.id === id);
      if (!project) {
        set({ error: 'Project not found', isLoading: false });
        return;
      }
      set({
        activeProjectId: id,
        activeProject: project,
        openFileIds: [],
        activeFileId: null,
        isLoading: false,
      });
      saveWorkspaceState({ activeProjectId: id, openFileIds: [], activeFileId: null });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to open project', isLoading: false });
    }
  },

  closeProject: () => {
    set({ activeProjectId: null, activeProject: null, openFileIds: [], activeFileId: null });
    clearWorkspaceState();
  },

  deleteProject: async (id) => {
    // Check if project has registered modules — cannot delete
    const all = await loadAllProjects();
    const project = all.find(p => p.id === id);
    if (project && (project.registeredModules ?? []).length > 0) {
      return; // Silently refuse — UI should prevent this
    }
    await deleteProjectFromDB(id);
    set(s => {
      const projects = s.projects.filter(p => p.id !== id);
      const reset = s.activeProjectId === id
        ? { activeProjectId: null, activeProject: null, openFileIds: [], activeFileId: null }
        : {};
      return { projects, ...reset };
    });
  },

  renameProject: async (id, name) => {
    const all = await loadAllProjects();
    const project = all.find(p => p.id === id);
    if (!project) return;
    project.name = name;
    project.updatedAt = Date.now();
    await persistProject(project);
    set(s => ({
      projects: s.projects.map(p => p.id === id ? { ...p, name, updatedAt: project.updatedAt } : p),
      activeProject: s.activeProjectId === id ? project : s.activeProject,
    }));
  },

  addFile: async (name, path, content) => {
    const project = get().activeProject;
    if (!project) return;
    const file: ProjectFile = {
      id: generateId(),
      name,
      path: path ? `${path}/${name}` : name,
      content: content ?? '',
      language: detectLanguage(name),
      isModified: false,
    };
    project.files.push(file);
    project.updatedAt = Date.now();
    await persistProject(project);
    set(s => ({
      activeProject: { ...project },
      projects: s.projects.map(p => p.id === project.id ? { ...p, fileCount: project.files.length, updatedAt: project.updatedAt } : p),
    }));
  },

  updateFileContent: (fileId, content) => {
    const project = get().activeProject;
    if (!project) return;
    const file = project.files.find(f => f.id === fileId);
    if (!file) return;
    file.content = content;
    file.isModified = true;
    set({ activeProject: { ...project } });
  },

  deleteFile: async (fileId) => {
    const project = get().activeProject;
    if (!project) return;
    project.files = project.files.filter(f => f.id !== fileId);
    project.updatedAt = Date.now();
    await persistProject(project);
    const newOpenFileIds = get().openFileIds.filter(id => id !== fileId);
    const newActiveFileId = get().activeFileId === fileId ? (newOpenFileIds[newOpenFileIds.length - 1] ?? null) : get().activeFileId;
    saveWorkspaceState({ openFileIds: newOpenFileIds, activeFileId: newActiveFileId });
    set(s => ({
      activeProject: { ...project },
      openFileIds: s.openFileIds.filter(id => id !== fileId),
      activeFileId: s.activeFileId === fileId ? (s.openFileIds.filter(id => id !== fileId)[0] ?? null) : s.activeFileId,
      projects: s.projects.map(p => p.id === project.id ? { ...p, fileCount: project.files.length, updatedAt: project.updatedAt } : p),
    }));
  },

  openFile: (fileId) => {
    set(s => {
      const ids = s.openFileIds.includes(fileId) ? s.openFileIds : [...s.openFileIds, fileId];
      saveWorkspaceState({ openFileIds: ids, activeFileId: fileId });
      return { openFileIds: ids, activeFileId: fileId };
    });
  },

  closeFile: (fileId) => {
    set(s => {
      const ids = s.openFileIds.filter(id => id !== fileId);
      const activeFileId = s.activeFileId === fileId ? (ids[ids.length - 1] ?? null) : s.activeFileId;
      saveWorkspaceState({ openFileIds: ids, activeFileId });
      return { openFileIds: ids, activeFileId };
    });
  },

  setActiveFile: (fileId) => {
    set({ activeFileId: fileId });
    saveWorkspaceState({ activeFileId: fileId });
  },

  saveProject: async () => {
    const project = get().activeProject;
    if (!project) return;
    project.files.forEach(f => { f.isModified = false; });
    project.updatedAt = Date.now();
    await persistProject(project);
    set(s => ({
      activeProject: { ...project },
      projects: s.projects.map(p => p.id === project.id ? { ...p, updatedAt: project.updatedAt } : p),
    }));
  },

  addPrefabToProject: async (config: ProjectPrefabConfig) => {
    const project = get().activeProject;
    if (!project) return;
    const prefabs = [...(project.addedPrefabs ?? []), config];
    project.addedPrefabs = prefabs;
    project.updatedAt = Date.now();
    await persistProject(project);
    set(s => ({
      activeProject: { ...project },
      projects: s.projects.map(p => p.id === project.id
        ? { ...p, updatedAt: project.updatedAt, prefabCount: prefabs.length }
        : p,
      ),
    }));
  },

  removePrefabFromProject: async (prefabId: string) => {
    const project = get().activeProject;
    if (!project) return;
    const idx = (project.addedPrefabs ?? []).findIndex(p => p.prefabId === prefabId && p.addedAt === (project.addedPrefabs ?? []).find(pp => pp.prefabId === prefabId)?.addedAt);
    if (idx === -1) return;
    project.addedPrefabs.splice(idx, 1);
    project.updatedAt = Date.now();
    await persistProject(project);
    set(s => ({
      activeProject: { ...project },
      projects: s.projects.map(p => p.id === project.id
        ? { ...p, updatedAt: project.updatedAt, prefabCount: project.addedPrefabs.length }
        : p,
      ),
    }));
  },

  setRegistrationStatus: async (status: ProjectRegistrationStatus, registeredProjectId?: string, modules?: RegisteredModule[]) => {
    const project = get().activeProject;
    if (!project) return;
    project.registrationStatus = status;
    if (registeredProjectId) {
      project.registeredProjectId = registeredProjectId;
    }
    if (modules) {
      project.registeredModules = modules;
    }
    project.updatedAt = Date.now();
    await persistProject(project);
    set(s => ({
      activeProject: { ...project },
      projects: s.projects.map(p => p.id === project.id
        ? { ...p, registrationStatus: status, updatedAt: project.updatedAt }
        : p,
      ),
    }));
  },

  registerModule: async (prefabId: string, module: RegisteredModule) => {
    const project = get().activeProject;
    if (!project) return;
    const existing = project.registeredModules ?? [];
    // Replace if already exists for same prefabId, else append
    const idx = existing.findIndex(m => m.prefabId === prefabId);
    if (idx >= 0) {
      existing[idx] = module;
    } else {
      existing.push(module);
    }
    project.registeredModules = existing;
    project.registrationStatus = 'registered';
    project.updatedAt = Date.now();
    await persistProject(project);
    set(s => ({
      activeProject: { ...project },
      projects: s.projects.map(p => p.id === project.id
        ? { ...p, registrationStatus: 'registered', updatedAt: project.updatedAt }
        : p,
      ),
    }));
  },

  hasRegisteredModules: () => {
    const project = get().activeProject;
    return (project?.registeredModules ?? []).length > 0;
  },
}));
